/* hlp-feature-cards — carousel controller. Loaded only when a carousel layout
   is active. Native scroll-snap does the scrolling; this wires arrows + dots +
   end-state. Custom element re-inits cleanly on shopify:section:load. */
(() => {
  class FeatureCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.drl-fc__track');
      if (!this.track) return;
      this.cards = [...this.track.children];
      this.arrows = [...this.querySelectorAll('.drl-fc__arrow')];
      this.dotsWrap = this.querySelector('.drl-fc__dots');
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.arrows.forEach((btn) =>
        btn.addEventListener('click', () => this.page(Number(btn.dataset.dir) || 1))
      );
      if (this.dotsWrap) this.buildDots();

      this.onScroll = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
    }

    page(dir) {
      this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: this.reduce ? 'auto' : 'smooth' });
    }

    buildDots() {
      this.dots = this.cards.map((card, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'drl-fc__dot';
        d.setAttribute('role', 'tab');
        d.setAttribute('aria-label', `${i + 1}`);
        d.addEventListener('click', () =>
          card.scrollIntoView({ behavior: this.reduce ? 'auto' : 'smooth', inline: 'start', block: 'nearest' })
        );
        this.dotsWrap.appendChild(d);
        return d;
      });
    }

    update() {
      const { scrollLeft, clientWidth, scrollWidth } = this.track;
      const atStart = scrollLeft <= 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
      this.arrows.forEach((btn) => {
        const next = (Number(btn.dataset.dir) || 1) > 0;
        btn.disabled = next ? atEnd : atStart;
      });
      if (this.dots) {
        const trackLeft = this.track.offsetLeft;
        let active = 0;
        let min = Infinity;
        this.cards.forEach((card, i) => {
          const d = Math.abs(card.offsetLeft - trackLeft - scrollLeft);
          if (d < min) { min = d; active = i; }
        });
        this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', i === active));
      }
    }
  }

  if (!customElements.get('drl-fc-carousel')) customElements.define('drl-fc-carousel', FeatureCarousel);

  /* Lazy-play: video loads (preload=none) + plays only while in view, pauses
     when scrolled out. Works in grid AND carousel. Self-upgrades per instance. */
  class FeatureVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true; // required for gesture-free play
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return; // poster only
      this.io = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (e.isIntersecting) this.video.play().catch(() => {});
          else this.video.pause();
        }),
        { threshold: 0.25 }
      );
      this.io.observe(this);
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
  }
  if (!customElements.get('drl-fc-video')) customElements.define('drl-fc-video', FeatureVideo);
})();
